/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {CommonModule} from '@angular/common';
import {HttpClient, HttpClientModule} from '@angular/common/http';
import {APP_INITIALIZER, enableProdMode, NgModule} from '@angular/core';
import {FlexModule} from '@angular/flex-layout';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatDialogModule} from '@angular/material/dialog';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSelectModule} from '@angular/material/select';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatSortModule} from '@angular/material/sort';
import {MatTableModule} from '@angular/material/table';
import {MatTooltipModule} from '@angular/material/tooltip';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {FabBreadcrumbsModule} from '@fabric/components/breadcrumbs';
import {APP_NAV_ITEMS} from '@fabric/components/common';
import {FabHeaderModule} from '@fabric/components/header';
import {
    FabAppInfoModule,
    FabBasicHeaderActionModule,
    FabThemeSettingModule,
    FabUserInfoModule,
} from '@fabric/components/header-actions';
import {FabLoadingSpinnerModule} from '@fabric/components/loading-spinner';
import {FabTopnavModule} from '@fabric/components/topnav';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {MatTableFilterModule} from 'mat-table-filter';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {appNavItems} from './core/config/app-nav-items';
import {HomeModule} from './home/home.module';
import {AppConfigService} from './core/config/app-config.service';
import {FAB_THEME_SETTING} from '@fabric/components/theme';
import {AuthenticationService} from './core/authentication/authentication.service';
import {OAuthModule} from 'angular-oauth2-oidc';
import {SharedRoutingModule} from './shared/shared-routing.module';
import {MatCardModule} from '@angular/material/card';
import {AdminComponent} from './admin/admin.component';
import {PersonalComponent} from './personal/personal.component';
import {PersonalPopUpComponent} from './personal/personal-pop-up/personal-pop-up.component';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MAT_DATE_LOCALE, MatNativeDateModule} from '@angular/material/core';
import {MatSelectFilterModule} from 'mat-select-filter';
import {CancelPopUpComponent} from './cancel-pop-up/cancel-pop-up.component';
import {WorkAbroadComponent} from './work-abroad/work-abroad.component';
import {WorkAbroadPopUpComponent} from './work-abroad/work-abroad-pop-up/work-abroad-pop-up.component';
import {MatMenuModule} from '@angular/material/menu';
import {ManagerComponent} from './manager/manager.component';
import {MatTabsModule} from '@angular/material/tabs';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {CdkTableModule} from '@angular/cdk/table';
import {AllRequestPopUpComponent} from './manager/allrequest-pop-up/all-request-pop-up.component';
import {HrComponent} from './hr/hr.component';
import {NewEmployeePopUpComponent} from './hr/new-employee-pop-up/new-employee-pop-up.component';
import {NewTeamPopUpComponent} from './hr/new-team-pop-up/new-team-pop-up.component';
import {NewPublicHolidayComponent} from './hr/new-public-holiday/new-public-holiday.component';
import {EmployeeDetailsPopUpComponent} from './hr/employee-details-pop-up/employee-details-pop-up.component';
import {EditEmployeePopUpComponent} from './hr/edit-employee-pop-up/edit-employee-pop-up.component';
import {EditTeamPopUpComponent} from './hr/edit-team-pop-up/edit-team-pop-up.component';
import {AbsenceCalendarPopUpComponent} from './absence-calendar-pop-up/absence-calendar-pop-up.component';
import {
    EmployeeVacationAllowanceOverrideComponent
} from './hr/employee-vacation-allowance-override/employee-vacation-allowance-override.component';
import {RemoveUnderscorePipePipe} from './pipe/remove-underscore-pipe.pipe';
import {DeclinePopUpComponent} from './decline-pop-up/decline-pop-up.component';
import { RequestCancelDecisionPopUpComponent } from './manager/request-cancel-decision-pop-up/request-cancel-decision-pop-up.component';
import { DropzoneDirective } from './hr/dropzone.directive';
import {EditWorkAbroadComponent} from './hr/edit-work-abroad-pop-up/edit-work-abroad-pop-up.component';
import { SearchPipe } from './pipe/search.pipe';


//** AoT requires an exported function for factories
export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, '../assets/languages/', '.json');
}

const appInitializerFn = (appConfig: AppConfigService, authenticationService: AuthenticationService) => {
    // Init functions must return a promise. Because the auth service depends on the config loading we need nested
    // promises here.
    return () => {
        return appConfig.loadAppConfig().then(() => {
            return authenticationService.initialize().then(isAuthenticated => {
                if (!isAuthenticated) {
                    throw 'Access Denied';
                }

                if (appConfig.getConfig().production) {
                    enableProdMode();
                }
            });
        });
    };
};

@NgModule({
    declarations: [AppComponent, PersonalComponent, PersonalPopUpComponent, AdminComponent, EmployeeVacationAllowanceOverrideComponent, CancelPopUpComponent, ManagerComponent, AllRequestPopUpComponent, WorkAbroadComponent, WorkAbroadPopUpComponent, HrComponent, NewEmployeePopUpComponent, NewTeamPopUpComponent,
        NewPublicHolidayComponent, EditWorkAbroadComponent, EmployeeDetailsPopUpComponent, EditEmployeePopUpComponent, EditTeamPopUpComponent, AbsenceCalendarPopUpComponent, RemoveUnderscorePipePipe, DeclinePopUpComponent, DropzoneDirective, RequestCancelDecisionPopUpComponent, SearchPipe],
    imports: [
        OAuthModule.forRoot(),
        BrowserModule,
        BrowserAnimationsModule,
        CommonModule,
        FabBreadcrumbsModule,
        FabAppInfoModule,
        FabHeaderModule,
        FabTopnavModule,
        FabThemeSettingModule,
        FabUserInfoModule,
        FabLoadingSpinnerModule,
        HomeModule,
        HttpClientModule,
        MatSnackBarModule,
        MatNativeDateModule,
        MatFormFieldModule,
        MatProgressBarModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
        }),
        AppRoutingModule,
        MatTableModule,
        MatSelectModule,
        FlexModule,
        MatDividerModule,
        MatPaginatorModule,
        MatProgressSpinnerModule,
        MatButtonModule,
        MatSortModule,
        ReactiveFormsModule,
        MatTableFilterModule,
        MatIconModule,
        MatDialogModule,
        FormsModule,
        MatInputModule,
        MatTooltipModule,
        SharedRoutingModule,
        MatCardModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatTabsModule,
        MatSlideToggleModule,
        CdkTableModule,
        MatSelectFilterModule,
        MatMenuModule,
        FabBasicHeaderActionModule
    ],
  providers: [
    AppConfigService,
    AuthenticationService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFn,
      multi: true,
      deps: [AppConfigService, AuthenticationService]
    },
    {provide: APP_NAV_ITEMS, useValue: appNavItems},
      {provide: MAT_DATE_LOCALE, useValue: 'en-GB'},
    {
      provide: FAB_THEME_SETTING,
      useValue: 'light'
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
